import { useCallback, useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import clsx from 'clsx'
import { nanoid } from 'nanoid'

import { RoutingPaths } from 'app/routes/routes.ts'

import { DirectionCard } from 'pages/promo-page/component/direction-card'
import { PromoSkeleton } from 'pages/promo-page/component/promo-skeleton'
import { TextCard } from 'pages/promo-page/component/text-card'
import { hasPromoAction } from 'pages/promo-page/lib/hasPromoAction'
import { onSearchFromPromo } from 'pages/promo-page/lib/onSearchFromPromo'

import { MainPageAvia, PromoUrlType } from 'shared/api/avia/main-page'
import { useRequest } from 'shared/hooks/use-request'
import { openDeepLink } from 'shared/lib/deep-link-open'
import { getApiErrorMessage } from 'shared/lib/errorText'
import { openExternalUrl } from 'shared/lib/external-open'
import { previewPdfUrl } from 'shared/lib/pdf-preview'
import { Button } from 'shared/ui/button'
import { EmptyPage } from 'shared/ui/empty-page'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { ImageLazy } from 'shared/ui/image-lazy'

import { PromoContentType } from './model'
import type { AirlineContent, PromoPage as Page, TextContent } from './model'
import s from './styles.module.scss'

const PromoPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams() as { id: string }
  const { data: promoResponse, error, isLoading, run } = useRequest<Page>()

  const loadPromo = useCallback(() => {
    if (!id) return Promise.resolve(null)

    return run(async (signal) => {
      const { data: response } = await MainPageAvia.getPromoDetails(id, signal)

      return response.data
    })
  }, [id, run])

  useEffect(() => {
    void loadPromo()
  }, [loadPromo])

  const pagePromo = promoResponse?.id === id ? promoResponse : null
  let errorText: string | null = null

  if (error !== null) {
    errorText = getApiErrorMessage(error) || t('error_des')
  }

  const isPromoLoading = isLoading || (!pagePromo && !errorText)
  const hasAction = hasPromoAction(pagePromo)

  const onGoBack = () => {
    navigate(RoutingPaths.path())
  }

  const onAction = () => {
    if (!pagePromo) return

    switch (pagePromo.url_type) {
      case PromoUrlType.EXTERNAL:
        openExternalUrl(pagePromo.redirect_url)
        break
      case PromoUrlType.INTERNAL:
        if (pagePromo.redirect_url) navigate(pagePromo.redirect_url)
        break
      case PromoUrlType.MBANK_DEEPLINK:
        openDeepLink(pagePromo.redirect_url)
        break
      case PromoUrlType.PDF:
        previewPdfUrl(pagePromo.file)
        break
      default:
        break
    }
  }

  const handleSearchFromPromo = (data: AirlineContent) => {
    onSearchFromPromo(data, navigate)
  }

  const renderContent = () => {
    if (!pagePromo?.content) return null

    return pagePromo.content.map((item) => {
      switch (item.content_type) {
        case PromoContentType.TEXT:
          return (
            <TextCard
              data={item}
              key={nanoid()}
            />
          )

        case PromoContentType.AIRLINE:
          return (
            <DirectionCard
              data={item}
              key={nanoid()}
              onSearch={handleSearchFromPromo}
            />
          )

        default:
          return (
            <TextCard
              data={item as TextContent}
              key={nanoid()}
            />
          )
      }
    })
  }

  return (
    <div className={clsx(s.whiteContainer, 'container')}>
      <HeaderGoBack
        title={pagePromo?.title}
        onGoBack={onGoBack}
        buttonWhite
        isLoading={isPromoLoading}
      />

      {isPromoLoading ? (
        <PromoSkeleton />
      ) : errorText ? (
        <EmptyPage
          title={t('error')}
          subTitle={errorText}
          buttonText={t('try_again')}
          onClick={() => void loadPromo()}
        />
      ) : (
        <div className={s.content}>
          <ImageLazy
            className={s.banner}
            src={pagePromo?.baner}
            alt={pagePromo?.title}
          />

          {renderContent()}
        </div>
      )}
      {pagePromo && hasAction && (
        <Button
          borderRadius={'md'}
          className={s.redirectBtn}
          onClick={onAction}
        >
          {pagePromo.button_text || t('next')}
        </Button>
      )}
    </div>
  )
}

export default PromoPage
